import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import advancedFormat from "dayjs/plugin/advancedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import dayjs, { Dayjs } from "dayjs";
import { detect } from "detect-browser";
import * as amplitude from "@amplitude/analytics-browser";
import { KYDPixels } from "@common/interfaces/KYDEvent";
import { AdminTicketType } from "@common/interfaces/KYDTicket";
import posthog from "posthog-js";
import actions from "./actions";
import dinero from "dinero.js";
import libphonenumber from "libphonenumber-js";

import AmplitudeTransport from "./AmplitudeTransport";
import {
  AddToCart,
  InitiateCheckout,
  Purchase,
  CompleteRegistration,
} from "react-facebook-pixel";
import { sha256 } from "js-sha256";
import { CognitoUser } from "amazon-cognito-identity-js";
import { ActionType } from "./Common/Dialog";
import { CreateToastFnReturn } from "@chakra-ui/react";
import { AlgoliaSortOrder } from "@common/interfaces/Algolia";
import { SyncUpdateKeys } from "./actions";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(relativeTime);

export type TieredTicketTypes = { [ticket_type_id: string]: AdminTicketType[] };

enum AUTH_STATES {
  LOADING = "loading",
  AUTHENTICATED = "authenticated",
  NEEDS_AUTH = "needs_auth",
  ONBOARDING = "onboarding",
}

enum ModalType {
  INACTIVE = 0,
  SECURING_TICKETS = 1,
  ADD_TO_CART_SUCCESS = 2,
  SIGN_IN_PHONE_NUMBER = 3,
  SIGN_IN_SMS_CODE = 4,
  SMS_CODE_EXPLAINER = 5,
  EMAIL_NAME_INPUT = 6,
  EXPIRED_CART = 7,
  CLEAR_CART_CONFIRM = 8,
  CLEARING_CART = 9,
  SHOW_WALLET = 10,
  TRANSFER_TICKETS_PHONE = 11,
  TRANSFER_TICKETS_INVALID_PHONE = 12,
  TRANSFER_TICKETS_TRANSFER_SUCCESS = 13,
  TRANSFER_TICKETS_SELECT = 14,
  TRANSFER_TICKETS_CONFIRM = 15,
  TRANSFER_TICKETS_SUCCESS = 16,
  CREATING_REMINDER = 17,
  LIGHTBOX = 18,
  CHECKING_OUT = 19,
  CHECK_OUT_SUCCESS = 20,
  CHECK_OUT_FAILED = 21,
  CHECK_OUT_TIMED_OUT = 22,
}

enum ScannerState {
  INACTIVE = 0,
  ACCESS_CODE_REQUIRED = 1,
  VALIDATING_ACCESS_CODE = 2,
  INITIALIZING = 3,
  READY = 4,
  PROCESSING = 5,
  VALIDATED = 6,
  INVALID_TICKETS = 400,
  CHECKING_IN = 9,
  CHECK_IN_SUCCESS = 10,
  CHECK_IN_FAILED = 11,
}

const API_NAMES = {
  EVENT: "KYDEVENT",
  SCANNER: "KYDSCANNER",
  ADMIN: "KYDADMIN",
  ADMIN_ME: "KYDADMINME",
  ANALYTICS: "ANALYTICS",
};

const getCurrentParamsUrl = () => {
  const href = window.location.href;
  if (href) {
    const parts = href.split("?").shift()?.split("https://").pop()?.split("/");

    let analytics_domain;
    let event_id;
    let type;
    parts?.forEach((part, index) => {
      if (part.endsWith(".com") && index === 0) {
        analytics_domain = part.split(".")[1];
      } else if (index === 1 && part === "e" && parts.length > 2) {
        event_id = parts[2];
        type = "event";
      } else if (index === 1 && part === "bio") {
        event_id = parts[1];
        type = "bio";
      }
    });
    return { event_id, type, analytics_domain };
  }

  return {};
};

const analytics = {
  init: async () => {
    posthog.init(process.env.REACT_APP_POSTHOG_API_KEY, {
      api_host: `https://app.posthog.com`,
      debug: false,
    });

    await amplitude.init(process.env.REACT_APP_AMPLITUDE_ID!, undefined, {
      attribution: {
        trackPageViews: false,
      },
      appVersion: process.env.REACT_APP_VERSION!,
      transportProvider: new AmplitudeTransport(),
      logLevel: amplitude.Types.LogLevel.Verbose,
    });
  },
  setUser: (user_id: string, email?: string) => {
    try {
      amplitude.setUserId(user_id);
      posthog.identify(
        user_id, // Replace 'distinct_id' with your user's unique identifier
        { email } // optional: set additional user properties
      );
      if (email) {
        const identifyEvent = new amplitude.Identify();
        identifyEvent.set("email", email);
        amplitude.identify(identifyEvent);
      }
    } catch (err) {}
  },
  t: (name: string, props?: any) => {
    const browser = detect();
    const { event_id, type } = getCurrentParamsUrl();

    if (!props) {
      props = { event_id, type };
    }

    const updatedProps = {
      ...props,
      browser: browser && browser.name ? browser.name.toLowerCase() : "unknown",
    };

    const id = amplitude.getUserId();
    if (id) {
      const group = id.charCodeAt(0) % 2 === 0 ? "a" : "b";
      updatedProps.group = group;
    }
    try {
      amplitude.track(name, updatedProps);
      posthog.capture(name, updatedProps);
    } catch (err) {}
  },
  reset: () => {
    amplitude.reset();
    posthog.reset();
  },
};

const showErrorToast = (toast: any, message: string) => {
  toast({
    title: "Woops!",
    description: message,
    status: "error",
    duration: 5000,
    isClosable: true,
  });
};

const showSuccessToast = (toast: any, message: string) => {
  toast({
    title: "Success!",
    description: message,
    status: "success",
    duration: 5000,
    isClosable: true,
  });
};

const currentTz = () => {
  return dayjs.tz.guess();
};

/**
 * Checks to see if browser is currently running on iOS device
 */
const isiOSDevice = () => {
  const browser = detect();
  return browser && browser.os && browser.os.toLowerCase() === "ios";
};

/**
 * Checks native functions for Safari to see if the user is on Safari browser
 */
const isSafari = () => {
  const browser = detect();

  return (
    (browser && browser.name.toLowerCase() === "safari") ||
    (browser && browser.name.toLowerCase() === "ios")
  );
};

/**
 * Checks to see if the current browser is mobile chrome on iOS
 */
const isMobileChrome = () => {
  const browser = detect();

  if (browser) {
    const browserName = browser.name.toLowerCase();
    return isiOSDevice() && browserName === "crios";
  }

  return false;
};

/**
 * Checks to see if the current browser is mobile Safari
 */
const isMobileSafari = () => {
  const browser = detect();

  if (browser) {
    const browserName = browser.name.toLowerCase();
    return isiOSDevice() && (browserName === "ios" || browserName === "safari");
  }

  return false;
};

/**
 * Checks to see if the current browser is on a mobile device
 */
const isMobileDevice = () => {
  const { userAgent } = navigator;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    userAgent
  );
};

const shortAddress = (address: string) => {
  return `${address.slice(0, 5)}...${address.slice(address.length - 5)}`;
};

const loadGoogleMapsAPI = (key: string, callback?: () => void) => {
  const existingScript = document.getElementById("gmaps-api");
  if (!existingScript) {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
    script.id = "gmaps-api";
    script.async = true;
    document.head.appendChild(script);
    script.onload = () => {
      if (callback) callback();
    };
  } else if (callback) {
    callback();
  }
};

//24175242

const loadHubspotPixel = (key: string, callback?: (type: string) => void) => {
  const existingScript = document.getElementById("hs-script-loader");
  if (!existingScript) {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.defer = true;
    script.src = `//js.hs-scripts.com/${key}.js`;
    script.id = "hs-script-loader";
    document.body.appendChild(script);
    script.onload = () => {
      if (callback) callback("hubspot");
    };
  } else if (callback) {
    callback("hubspot");
  }
};

const scrollToTop = () => {
  document.body.scrollTop = 0; // For Safari
  document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
};

const setupFBPixel = (pixelId: string, callback: (type: string) => void) => {
  //const advancedMatching = { em: "some@email.com" }; // optional, more info: https://developers.facebook.com/docs/facebook-pixel/advanced/advanced-matching
  const options = {
    autoConfig: true, // set pixel's autoConfig. More info: https://developers.facebook.com/docs/facebook-pixel/advanced/
    debug: false, // enable logs
  };
  //ReactPixel.init(pixelId, undefined, options);
  callback("facebook");
};

const setupPixels = (pixels: KYDPixels, callback: (type: string) => void) => {
  console.log("Setup Pixels: ", pixels);
  if (pixels.facebook) {
    console.log("FB Pixel Complete");
    setupFBPixel(pixels.facebook.id, callback);
  }

  if (pixels.hubspot) {
    console.log("HS Pixel Complete");
    loadHubspotPixel(pixels.hubspot.id, callback);
  }
};

const isHubspotPixelInstalled = () => {
  //@ts-ignore
  return typeof window !== "undefined" && window._hsq;
};

const isFacebookPixelInstalled = () => {
  //@ts-ignore
  return typeof window !== "undefined" && window.fbq;
};

const isSnapchatPixelInstalled = () => {
  //@ts-ignore
  return typeof window !== "undefined" && window.snaptr;
};

const isTikTokPixelInstalled = () => {
  return (
    typeof window !== "undefined" &&
    (window as any).ttq &&
    (window as any).ttq.track &&
    (window as any).ttq.identify
  );
};

const isTwitterPixelInstalled = () => {
  //@ts-ignore
  return typeof window !== "undefined" && window.twq;
};

const pixel_trackPurchase = (
  data?: Purchase,
  eventId?: string | null,
  twitterEventId?: string | null,
  currentUser?: CognitoUser | null
) => {
  try {
    if (isFacebookPixelInstalled()) {
      //@ts-ignore
      window.fbq("track", "Purchase", data, { eventID: eventId }); // eslint-disable-line no-undef
    } else {
      console.log("FB Pixel not installed");
    }

    if (isHubspotPixelInstalled()) {
      //@ts-ignore
      window._hsq.push([
        "trackEvent",
        {
          id: "Purchase",
          value: data?.value,
        },
      ]);
    }

    if (isTikTokPixelInstalled()) {
      //@ts-ignore
      window.ttq.track("PlaceAnOrder", data); // eslint-disable-line no-undef
    }

    if (isSnapchatPixelInstalled()) {
      //@ts-ignore
      window.snaptr("track", "PURCHASE", {
        currency: "USD",
        price: data?.value,
        item_category: data?.content_type,
        //@ts-ignore
        item_ids: data?.contents?.map((c) => c.id),
        client_dedup_id: eventId,
        transaction_id: eventId,
      });
    }

    if (isTwitterPixelInstalled() && twitterEventId) {
      if (data) {
        //@ts-ignore
        data.conversion_id = eventId;
      }

      if (currentUser) {
        currentUser.getUserAttributes((err, results) => {
          if (!err && results && results.length > 0) {
            const email = results.find((result) => result.Name === "email");

            //@ts-ignore
            data.email_address = email ? email.Value : null;
            //@ts-ignore
            data.phone_number = currentUser.getUsername();

            //@ts-ignore
            window.twq("event", twitterEventId, data); // eslint-disable-line no-undef
          } else {
            //@ts-ignore
            window.twq("event", twitterEventId, data); // eslint-disable-line no-undef
          }
        });
      } else {
        //@ts-ignore
        window.twq("event", twitterEventId, data); // eslint-disable-line no-undef
      }
    }
  } catch (err) {
    console.error(err);
  }
};

const pixel_pageView = () => {
  try {
    if (isTikTokPixelInstalled()) {
      //@ts-ignore
      window.ttq.track("ViewContent"); // eslint-disable-line no-undef
    }

    if (isSnapchatPixelInstalled()) {
      //@ts-ignore
      window.snaptr("track", "VIEW_CONTENT");
    }
  } catch (err) {
    console.error(err);
  }
};

const pixel_addToCart = (
  data?: AddToCart,
  eventId?: string | null,
  twitterEventId?: string | null
) => {
  try {
    if (isFacebookPixelInstalled()) {
      //@ts-ignore
      window.fbq("track", "AddToCart", data, eventId); // eslint-disable-line no-undef
    } else {
      console.log("FB Pixel not installed");
    }

    if (isHubspotPixelInstalled()) {
      //@ts-ignore
      window._hsq.push([
        "trackEvent",
        {
          id: "AddToCart",
          value: data?.value,
        },
      ]);
    }

    if (isTikTokPixelInstalled()) {
      //@ts-ignore
      window.ttq.track("AddToCart", data); // eslint-disable-line no-undef
    }

    if (isTwitterPixelInstalled() && twitterEventId) {
      if (data) {
        //@ts-ignore
        data.conversion_id = eventId;
      }
      //@ts-ignore
      window.twq("event", twitterEventId, data); // eslint-disable-line no-undef
    }

    if (isSnapchatPixelInstalled()) {
      //@ts-ignore
      window.snaptr("track", "ADD_CART", {
        currency: "USD",
        price: data?.value,
        //@ts-ignore
        item_ids: data?.contents?.map((c) => c.id),
      });
    }
  } catch (err) {
    console.error(err);
  }
};

const pixel_setReminder = (data?: any) => {
  try {
    if (isFacebookPixelInstalled()) {
      //@ts-ignore
      window.fbq("trackCustom", "SetReminder", data); // eslint-disable-line no-undef
    } else {
      console.log("FB Pixel not installed");
    }

    if (isTikTokPixelInstalled()) {
      //@ts-ignore
      window.ttq.track("SetReminder", data); // eslint-disable-line no-undef
    }

    if (isSnapchatPixelInstalled()) {
      //@ts-ignore
      window.snaptr("track", "SetReminder");
    }
  } catch (err) {
    console.error(err);
  }
};

const pixel_initiateCheckout = (
  data?: InitiateCheckout,
  eventId?: string | null,
  twitterEventId?: string | null
) => {
  try {
    if (isFacebookPixelInstalled()) {
      //@ts-ignore
      window.fbq("track", "InitiateCheckout", data, eventId); // eslint-disable-line no-undef
    } else {
      console.log("FB Pixel not installed");
    }

    if (isHubspotPixelInstalled()) {
      //@ts-ignore
      window._hsq.push([
        "trackEvent",
        {
          id: "InitiateCheckout",
          value: null,
        },
      ]);
    }

    if (isTikTokPixelInstalled()) {
      //@ts-ignore
      window.ttq.track("InitiateCheckout", data); // eslint-disable-line no-undef
    }

    if (isTwitterPixelInstalled() && twitterEventId) {
      if (data) {
        //@ts-ignore
        data.conversion_id = eventId;
      }
      //@ts-ignore
      window.twq("event", twitterEventId, data); // eslint-disable-line no-undef
    }

    if (isSnapchatPixelInstalled()) {
      //@ts-ignore
      window.snaptr("track", "START_CHECKOUT", {
        currency: "USD",
        price: data?.value,
        //@ts-ignore
        item_ids: data?.contents?.map((c) => c.id),
      });
    }
  } catch (err) {
    console.error(err);
  }
};

const pixel_completeRegistration = (data?: CompleteRegistration) => {
  try {
    if (isFacebookPixelInstalled()) {
      //@ts-ignore
      window.fbq("track", "CompleteRegistration", data); // eslint-disable-line no-undef
    } else {
      console.log("FB Pixel not installed");
    }

    if (isHubspotPixelInstalled()) {
      //@ts-ignore
      window._hsq.push([
        "trackEvent",
        {
          id: "CompleteRegistration",
          value: null,
        },
      ]);
    }

    if (isTikTokPixelInstalled()) {
      //@ts-ignore
      window.ttq.track("CompleteRegistration"); // eslint-disable-line no-undef
    }
  } catch (err) {
    console.error(err);
  }
};

const pixel_identify = ({
  user_id,
  email,
  phone_number,
}: {
  user_id?: string | null;
  email?: string | null;
  phone_number?: string | null;
}) => {
  try {
    if (isHubspotPixelInstalled() && email) {
      //@ts-ignore
      window._hsq.push([
        "identify",
        {
          email,
        },
      ]);
    }

    //@ts-ignore
    if (isFacebookPixelInstalled() && window.fbq.getState) {
      //@ts-ignore
      const pixels_setup = window.fbq.getState();
      if (
        pixels_setup &&
        pixels_setup.pixels &&
        pixels_setup.pixels.length > 0
      ) {
        pixels_setup.pixels.forEach((pixel_data: any) => {
          const { em, ph, external_id } = pixel_data.userData;

          let hashed_email = email ? sha256(email) : null;
          let hashed_user_id = user_id ? sha256(user_id) : null;
          let hashed_phone_number = phone_number
            ? sha256(phone_number.slice(1))
            : null;

          if (em && !hashed_email) {
            hashed_email = em;
          }

          if (external_id && !hashed_user_id) {
            hashed_user_id = external_id;
          }

          if (ph && !hashed_phone_number) {
            hashed_phone_number = ph;
          }

          //@ts-ignore
          window.fbq("init", pixel_data.id, {
            external_id: hashed_user_id,
            em: hashed_email,
            ph: hashed_phone_number,
          });
        });
      }
    }

    if (isTikTokPixelInstalled()) {
      let hashed_email = email ? sha256(email) : null;
      let hashed_phone_number = phone_number ? sha256(phone_number) : null;
      //@ts-ignore
      window.ttq.identify({
        sha256_email: hashed_email,
        sha256_phone_number: hashed_phone_number,
        external_id: user_id,
      });
    }

    try {
      if (isSnapchatPixelInstalled()) {
        let hashed_email = email ? sha256(email.toLowerCase()) : null;
        let hashed_phone_number = phone_number
          ? sha256(phone_number.slice(1))
          : null;
        //@ts-ignore
        if (window.snaptr.context && window.snaptr.context.$) {
          //@ts-ignore
          const ids = Object.keys(window.snaptr.context.$);
          if (ids.length > 0) {
            ids.forEach((id) => {
              //@ts-ignore
              window.snaptr("init", id, {
                user_hashed_email: hashed_email,
                user_hashed_phone_number: hashed_phone_number,
              });
            });
          } else {
            analytics.t("pixels: error - no pixel id", {
              type: "snapchat",
            });
          }
        } else {
          analytics.t("pixels: error - no pixel code", { type: "snapchat" });
        }
      }
    } catch (err) {
      console.error("Could not identify snapchat pixel");
    }
  } catch (err) {
    console.error(err);
  }
};

const PIXELS = {
  setup: setupPixels,
  purchase: pixel_trackPurchase,
  pageview: pixel_pageView,
  addtocart: pixel_addToCart,
  initiatecheckout: pixel_initiateCheckout,
  completeregistration: pixel_completeRegistration,
  identify: pixel_identify,
  setReminder: pixel_setReminder,
};

// dec2hex :: Integer -> String
// i.e. 0-255 -> '00'-'ff'
const dec2hex = (dec: number) => {
  return dec.toString(16).padStart(2, "0");
};

// generateId :: Integer -> String
const getRandomString = (len: number) => {
  var arr = new Uint8Array((len || 40) / 2);
  crypto.getRandomValues(arr);
  return Array.from(arr, dec2hex).join("");
};

const capitalized = (word: string) =>
  word ? word.charAt(0).toUpperCase() + word.slice(1) : "";

export enum EditModalType {
  INACTIVE,
  DESCRIPTION,
  TICKET_TYPE,
  PROMO_CODE,
  ACCESS_CODE,
  CREATE_AFFILIATES,
  VENUE,
  EDIT_TIERED_TICKET_TYPE,
  NAME,
  DATE,
  TIMELINE,
  DUPLICATE,
  RESCHEDULING_CAMPAIGN,
  CONFIRMATION_EMAIL,
}

const formatDate = (date: Dayjs, format: string = "ddd MMM DD hh:mmA") =>
  date.format(format);

const formatDateForCampaign = (date?: string, timezone?: string) => {
  return dayjs(date).tz(timezone).format("ddd, MMMM D [at] h:mmA");
};

export function createOnUpdateEvent({
  dialog,
  event_id,
  pollForEventUpdateStatus,
  refetch,
  setLoading,
  setModalType,
  toast,
}: {
  event_id: string;
  setLoading: (isLoading: boolean) => void;
  refetch: (options?: any) => Promise<any>;
  pollForEventUpdateStatus: () => Promise<boolean>;
  setModalType: (modalType: EditModalType) => void;
  dialog: (params: {
    title?: string;
    text?: string;
    actions?: ActionType[];
  }) => any;
  toast: CreateToastFnReturn;
}) {
  const syncUpdateProps: SyncUpdateKeys[] = [
    "name",
    "subtitle",
    "age_restriction",
    "description",
    "description_rich",
  ];
  return async (key: SyncUpdateKeys, override_value: any) => {
    setLoading(true);
    try {
      const value = override_value;

      if (value !== undefined) {
        if (syncUpdateProps.includes(key)) {
          await actions.ADMIN.updateEventSync(
            event_id!,
            key as SyncUpdateKeys,
            value
          );
          await refetch({ silent: true });
          toast({
            title: "Updated",
            description: "Event has been updated",
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        } else {
          const result = await actions.ADMIN.updateEvent(event_id!, key, value);
          if (result && result.update_id) {
            const params = new URLSearchParams(window.location.search);
            params.set("update_id", result.update_id);

            const newUrl =
              window.location.protocol +
              "//" +
              window.location.host +
              window.location.pathname +
              "?" +
              params.toString();
            window.history.pushState({ path: newUrl }, "", newUrl);

            const success = await pollForEventUpdateStatus();
            if (success) {
              console.log("Updated Event");
              toast({
                title: "Updated",
                description: "Event has been updated",
                status: "success",
                duration: 5000,
                isClosable: true,
              });
            }
          } else {
            await refetch({ silent: true });
          }
        }
      }
    } catch (err) {
      dialog({ text: err.message });
    }
    setLoading(false);
    setModalType(EditModalType.INACTIVE);
  };
}

export const isTicketTypeSystem = (ticketTypeId: string = "") =>
  ticketTypeId?.startsWith("SS");

export const markdownToLineBreaks = (markdownText: string = "") => {
  // KUDOS: https://github.com/remarkjs/react-markdown/issues/278#issuecomment-1504554666
  return markdownText?.replace(/(?<=\n\n)(?![*-])\n/gi, "&nbsp;\n ");
};

export const formatCurrency = (value: number) => {
  const formatter = new Intl.NumberFormat("en-US", {
    notation: "compact",
  });
  return "$" + formatter.format(value);
};

export const formatNumber = (value: number) => {
  const formatter = new Intl.NumberFormat("en-US");
  return formatter.format(value);
};

export const dineroFormatCurrency = (value: number) => {
  if (value === null || value === undefined) return "$0.00";

  const formatter = new Intl.NumberFormat("en-US", {
    currency: "USD",
    style: "currency",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  const units = dinero({ amount: value, currency: "USD" }).toUnit();
  return formatter.format(units);
};

export const colorForPercentage = (percentage: number) => {
  if (percentage <= 30) return "red";
  if (percentage <= 60) return "yellow";
  if (percentage > 60) return "green";
};

export const handleIndexNameTransform = (
  indexName: string,
  attribute: string,
  order: AlgoliaSortOrder,
  defaultState: {
    sortBy: string;
    sortOrder: AlgoliaSortOrder;
  }
) => {
  // If attribute or order are in default state, return primary index name
  const primaryIndexName = indexName?.split("_")[0];
  if (attribute === defaultState.sortBy && order === defaultState.sortOrder) {
    return primaryIndexName;
  }

  return `${primaryIndexName}_${attribute}_${order}`;
};

export const formatPhoneNumber = (
  phone_number: string,
  mode?: "national" | "international"
) => {
  const parsedPhoneNumber = libphonenumber(phone_number);
  if (parsedPhoneNumber && parsedPhoneNumber.isValid()) {
    if (mode) {
      if (mode === "national") {
        return parsedPhoneNumber.formatNational();
      } else if (mode === "international") {
        return parsedPhoneNumber.formatInternational();
      } else {
        throw Error(`Invalid mode: ${mode}`);
      }
    }

    return parsedPhoneNumber.formatNational();
  }
};

export {
  showErrorToast,
  showSuccessToast,
  isMobileDevice,
  currentTz,
  isSafari,
  isMobileSafari,
  isiOSDevice,
  isMobileChrome,
  shortAddress,
  AUTH_STATES,
  ModalType,
  ScannerState,
  analytics,
  API_NAMES,
  loadGoogleMapsAPI,
  scrollToTop,
  getRandomString,
  PIXELS,
  capitalized,
  getCurrentParamsUrl,
  formatDate,
  formatDateForCampaign,
};
